import { SuiteApiClient } from "@weco/suite-client";

/**
 * Mochi meeting-twin API (`services/assistant`'s `/api/v1.0/twin/*`).
 *
 * `@weco/suite-client`'s `apiRoutes.ts` has no `API_ASSISTANT_PREFIX` constant
 * (unlike `API_VIDEO_PREFIX` etc.) — the assistant module's own `ticketsApi.ts`
 * hits the same gateway prefix with a hardcoded `"/api/assistant"` base for the
 * same reason. The gateway maps `/api/assistant/*` -> the assistant service's
 * `/api/v1.0/*`.
 */
const API_ASSISTANT_PREFIX = "/api/assistant";

export const twinApi = new SuiteApiClient(API_ASSISTANT_PREFIX);

// Backend: `services/assistant/core/models.py`'s TwinDispatch.target_type
// comment documents the full set: room|office_room|event|email. "conversation"
// predates that comment (kept for back-compat with whatever already sends it).
export type TwinTargetType = "room" | "conversation" | "event" | "office_room";

export interface TwinDispatchRequest {
  target_type: TwinTargetType;
  target_ref: string;
  /** Twin participation mode (e.g. "observer" / "participant"); server-defaulted
   *  when omitted. */
  mode?: string;
  /** ISO-8601 — schedule the twin to join at a future time instead of now. */
  scheduled_for?: string;
  /** Optional free-text focus for the minutes summarizer (e.g. "decisions"). */
  focus?: string;
}

export interface TwinDispatch {
  id: string;
  target_type: TwinTargetType;
  target_ref: string;
  mode: string;
  status: string;
  scheduled_for?: string | null;
  created_at: string;
}

export const twinService = {
  /** POST /twin/dispatch/ — send Mochi into a room or conversation. */
  dispatch: async (body: TwinDispatchRequest): Promise<TwinDispatch> => {
    return twinApi.post("/twin/dispatch/", body);
  },

  /** POST /twin/dispatches/{id}/approve/ — approve a pending twin dispatch. */
  approve: async (id: string): Promise<TwinDispatch> => {
    return twinApi.post(`/twin/dispatches/${id}/approve/`, {});
  },

  /** POST /twin/dispatches/{id}/deny/ — deny a pending twin dispatch. */
  deny: async (id: string): Promise<TwinDispatch> => {
    return twinApi.post(`/twin/dispatches/${id}/deny/`, {});
  },

  /** POST /twin/dispatches/{id}/recall/ — pull Mochi back out of a meeting. */
  recall: async (id: string): Promise<TwinDispatch> => {
    return twinApi.post(`/twin/dispatches/${id}/recall/`, {});
  },
};
